import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus, Loader2, FileText, UploadCloud, History, Download, FileStack, X,
} from "lucide-react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { DOC_CATEGORIES, fileSize, timeAgo, initials } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

function NewDocDialog({ projectId, open, onOpenChange }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Architectural");

  const create = useMutation({
    mutationFn: async () => (await api.post(`/projects/${projectId}/documents`, { title, category })).data.document,
    onSuccess: () => {
      toast.success("Document created");
      qc.invalidateQueries({ queryKey: ["documents", projectId] });
      onOpenChange(false); setTitle("");
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border">
        <DialogHeader><DialogTitle className="font-display text-2xl font-black">New Document</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); create.mutate(); }} className="space-y-4">
          <div className="space-y-2">
            <Label className="label-caps">Title</Label>
            <Input required value={title} onChange={(e) => setTitle(e.target.value)} data-testid="doc-title-input" className="h-12 bg-background" placeholder="e.g. Level 3 Floor Plan" />
          </div>
          <div className="space-y-2">
            <Label className="label-caps">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-12 bg-background" data-testid="doc-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{DOC_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={create.isPending} data-testid="create-doc-submit"
              className="h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-bold w-full">
              {create.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create Document"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function VersionDialog({ doc, open, onOpenChange }) {
  const qc = useQueryClient();
  const fileRef = useRef();
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);

  const { data } = useQuery({
    queryKey: ["versions", doc?.id],
    queryFn: async () => (await api.get(`/documents/${doc.id}/versions`)).data.versions,
    enabled: !!doc && open,
  });

  const upload = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("commit_notes", notes);
      return (await api.post(`/documents/${doc.id}/versions`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data.version;
    },
    onSuccess: () => {
      toast.success("New version committed");
      qc.invalidateQueries({ queryKey: ["versions", doc.id] });
      qc.invalidateQueries({ queryKey: ["documents"] });
      setFile(null); setNotes(""); if (fileRef.current) fileRef.current.value = "";
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const download = async (v) => {
    try {
      const res = await api.get(`/document-versions/${v.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = v.file_name || `v${v.version_number}`;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  if (!doc) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black flex items-center gap-2">
            <FileStack className="h-6 w-6 text-primary" /> {doc.title}
          </DialogTitle>
          <div className="label-caps">{doc.category}</div>
        </DialogHeader>

        {/* Upload new version (append-only) */}
        <div className="border border-border rounded-md p-4 bg-background space-y-3">
          <Label className="label-caps flex items-center gap-1.5"><UploadCloud className="h-3.5 w-3.5" /> Commit New Version</Label>
          <Input ref={fileRef} type="file" onChange={(e) => setFile(e.target.files[0])} data-testid="version-file-input" className="bg-card h-12" />
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Commit notes / validation checks…" data-testid="version-notes-input" className="bg-card min-h-16" />
          <Button disabled={!file || upload.isPending} onClick={() => upload.mutate()} data-testid="upload-version-button"
            className="h-12 w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold">
            {upload.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : <><UploadCloud className="h-4 w-4 mr-1" /> Upload Version</>}
          </Button>
        </div>

        <div className="space-y-2">
          <Label className="label-caps flex items-center gap-1.5"><History className="h-3.5 w-3.5" /> Version Ledger (append-only)</Label>
          {!data || data.length === 0 ? (
            <p className="text-sm text-muted-foreground py-3">No versions yet. Upload the first file above.</p>
          ) : (
            <div className="space-y-2">
              {data.map((v) => (
                <div key={v.id} className="flex items-center gap-3 border border-border rounded px-3 py-2.5" data-testid={`version-row-${v.version_number}`}>
                  <span className="h-8 w-8 rounded bg-primary/20 text-primary grid place-items-center font-data font-bold text-xs shrink-0">v{v.version_number}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{v.file_name}</div>
                    <div className="text-xs text-muted-foreground">
                      {fileSize(v.file_size_bytes)} · .{v.file_extension} · {v.uploaded_by_user?.first_name || "?"} · {timeAgo(v.uploaded_at)}
                    </div>
                    {v.commit_notes && <div className="text-xs text-muted-foreground italic mt-0.5">“{v.commit_notes}”</div>}
                  </div>
                  <Button variant="ghost" size="icon" className="h-10 w-10 shrink-0" onClick={() => download(v)} data-testid={`download-version-${v.version_number}`}>
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function BlueprintsTab({ projectId }) {
  const [filter, setFilter] = useState("All");
  const [newOpen, setNewOpen] = useState(false);
  const [activeDoc, setActiveDoc] = useState(null);

  const { data: docs, isLoading } = useQuery({
    queryKey: ["documents", projectId],
    queryFn: async () => (await api.get(`/projects/${projectId}/documents`)).data.documents,
  });

  const categories = ["All", ...DOC_CATEGORIES];
  const filtered = (docs || []).filter((d) => filter === "All" || d.category === filter);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap gap-1.5">
          {categories.map((c) => (
            <button key={c} onClick={() => setFilter(c)} data-testid={`doc-filter-${c}`}
              className={`h-11 px-4 rounded-md text-sm font-semibold border transition-colors ${
                filter === c ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-card"
              }`}>{c}</button>
          ))}
        </div>
        <Button onClick={() => setNewOpen(true)} data-testid="new-doc-button" className="h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-bold">
          <Plus className="h-5 w-5 mr-1" /> New Document
        </Button>
      </div>

      {isLoading ? (
        <div className="grid place-items-center py-24"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : filtered.length === 0 ? (
        <div className="border border-border rounded-md bg-card p-12 text-center">
          <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-display text-xl font-bold">No documents</h3>
          <p className="text-muted-foreground mt-1">Create a document and upload version-controlled blueprints.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {filtered.map((d) => (
            <button key={d.id} onClick={() => setActiveDoc(d)} data-testid={`doc-card-${d.id}`}
              className="text-left bg-card border border-border rounded-md p-4 hover:border-primary transition-colors group">
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded bg-primary/15 grid place-items-center shrink-0">
                  <FileText className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold leading-tight group-hover:text-primary transition-colors truncate">{d.title}</div>
                  <div className="label-caps mt-1">{d.category}</div>
                </div>
              </div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-border text-sm">
                <span className="text-muted-foreground flex items-center gap-1.5">
                  <FileStack className="h-4 w-4" /> {d.version_count} version{d.version_count !== 1 ? "s" : ""}
                </span>
                {d.latest_version ? (
                  <span className="font-data text-xs text-primary">v{d.latest_version.version_number} · {fileSize(d.latest_version.file_size_bytes)}</span>
                ) : (
                  <span className="text-xs text-muted-foreground">empty</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      <NewDocDialog projectId={projectId} open={newOpen} onOpenChange={setNewOpen} />
      <VersionDialog doc={activeDoc} open={!!activeDoc} onOpenChange={(v) => !v && setActiveDoc(null)} />
    </div>
  );
}
